import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CNav,
  CNavItem,
  CNavLink,
  CSmartTable,
  CTabContent,
  CTabPane,
  CButton,
  CRow,
  CCol,
} from "@coreui/react-pro";
import toast, { Toaster } from "react-hot-toast";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* Helpers */
const useTranslate = (lang) =>
  useCallback((en, zh) => (lang === "en" ? en : zh), [lang]);

const buildPayload = ({ title, message, screen = "home" }) => ({
  notification: { title, body: message },
  data: { screen },
});

const normaliseUsers = (rows = []) =>
  rows.map((u) => ({
    id: u.ID,
    email: u.Email,
    name: u.Name,
    deviceToken: u.DeviceToken,
    createdAt: u.CreatedAt ? new Date(u.CreatedAt).toLocaleString() : "-",
    updatedAt:
      u.UpdatedAt && !isNaN(new Date(u.UpdatedAt))
        ? new Date(u.UpdatedAt).toLocaleString()
        : "-",
  }));

/* Component */
const NotificationSender = ({ lang = "en" }) => {
  const t = useTranslate(lang);

  const [notification, setNotification] = useState({
    title: "",
    message: "",
    screen: "home",
  });

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [csvMatches, setCsvMatches] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tab, setTab] = useState("csv");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await fetch(`${BASE_URL}/api/apn/devices`);
        if (!res.ok) throw new Error("Network response was not ok");
        const { data } = await res.json();
        setUsers(normaliseUsers(data));
      } catch (err) {
        console.error(err);
        toast.error(t("Failed to load devices.", "無法加載通知設備。"));
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [t]);

  const uploadAndExtract = async (f) => {
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      const res = await fetch(`${BASE_URL}/api/apn/extract-csv`, {
        method: "POST",
        body: formData,
      });
      const response = await res.json();
      const data = response.data;
      console.log(response);

      if (!res.ok) throw new Error(response?.message || "Extract failed");
      setCsvMatches(normaliseUsers(data));
      toast.success(
        t(
          `Found ${data.length} user(s) in database.`,
          `資料庫中找到 ${data.length} 位用戶。`
        )
      );
    } catch (err) {
      console.log(err);
      setCsvMatches([]);
      toast.error(err.message || t("Extract failed", "提取失敗"));
    } finally {
      setExtracting(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setCsvMatches([]);
    if (!f) {
      toast.error(t("No file selected.", "未選擇檔案。"));
      return;
    }
    toast.success(t("File selected.", "已選擇檔案。"));
    uploadAndExtract(f);
  };

  const handleUserSelect = (user, checked) =>
    setSelected((prev) =>
      checked ? [...prev, user] : prev.filter((u) => u.id !== user.id)
    );

  const handleInput = (e) =>
    setNotification((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSend = async () => {
    if (!notification.title || !notification.message) {
      toast.error(t("Title & message required.", "標題與訊息為必填。"));
      return;
    }
    if (tab === "csv" && !file) {
      toast.error(t("Choose a CSV first.", "請先選擇 CSV。"));
      return;
    }
    if (tab === "list" && selected.length === 0) {
      toast.error(t("Select at least one user.", "請至少選擇一位用戶。"));
      return;
    }

    setSending(true);
    try {
      const targetDevices = tab === "csv" ? csvMatches : selected;
      const res = await fetch(`${BASE_URL}/api/apn/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          devices: targetDevices,
          notificationPayload: buildPayload(notification),
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.message || "Send failed");

      toast.success(
        tab === "csv"
          ? t("Notification job queued!", "通知任務已提交！")
          : t(
              `Notification sent to ${selected.length} user(s)!`,
              `已向 ${selected.length} 位用戶發送通知！`
            )
      );

      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setCsvMatches([]);
      setSelected([]);
      setNotification({ title: "", message: "", screen: "home" });
    } catch (err) {
      console.error(err);
      toast.error("" + (err.message || t("Send failed", "發送失敗")));
    } finally {
      setSending(false);
    }
  };

  const listColumns = useMemo(
    () => [
      {
        key: "select",
        label: "",
        _style: { width: "5%" },
        filter: false,
        sorter: false,
      },

      { key: "name", label: t("Name", "名稱") },
      { key: "email", label: t("Email", "電子郵件") },
    ],
    [t]
  );

  const listScoped = useMemo(
    () => ({
      select: (item) => (
        <td>
          <CFormCheck
            checked={selected.some((u) => u.id === item.id)}
            onChange={(e) => handleUserSelect(item, e.target.checked)}
          />
        </td>
      ),
      name: (item) => <td>{item.name}</td>,
    }),
    [selected]
  );

  return (
    <div className="container-fluid p-4">
      <h1 className="h3 mb-4">{t("Send Notifications", "發送通知")}</h1>
      <CRow className="g-4">
        <CCol md={6}>
          <CCard className="h-100 d-flex flex-column">
            <CCardHeader className="p-0 border-0">
              <CNav variant="tabs" role="tablist">
                <CNavItem>
                  <CNavLink
                    active={tab === "csv"}
                    onClick={() => setTab("csv")}
                    role="button"
                  >
                    {t("Upload CSV", "上傳 CSV")}
                  </CNavLink>
                </CNavItem>
                <CNavItem>
                  <CNavLink
                    active={tab === "list"}
                    onClick={() => setTab("list")}
                    role="button"
                  >
                    {t("Pick from list", "從列表選擇")}
                  </CNavLink>
                </CNavItem>
              </CNav>
            </CCardHeader>
            <CCardBody className="flex-grow-1 overflow-auto">
              <CTabContent>
                <CTabPane visible={tab === "csv"}>
                  {/* Drag & Drop Zone */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const f = e.dataTransfer.files?.[0];
                      if (f) {
                        fileInputRef.current.files = e.dataTransfer.files;
                        handleFileChange({ target: { files: [f] } });
                      }
                    }}
                    className="border border-dashed border-2 border-primary rounded-3 p-4 text-center bg-light"
                    style={{ cursor: "pointer", position: "relative" }}
                  >
                    {/* Hidden native file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    <p className="text-muted mb-2">
                      {t(
                        "Drag & drop your CSV here, or click to upload",
                        "拖放或點擊上傳 CSV"
                      )}
                    </p>
                    <CButton color="primary" size="sm" variant="ghost">
                      {t("Browse Files", "選擇檔案")}
                    </CButton>
                    {file && (
                      <div className="mt-3 text-muted small">
                        <strong>{file.name}</strong> –{" "}
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    )}
                  </div>

                  {/* Extracted Result Preview */}
                  {extracting && (
                    <div className="mt-3">{t("Extracting...", "提取中…")}</div>
                  )}
                  {!extracting && csvMatches.length > 0 && (
                    <div className="mt-4">
                      <h6>
                        {t(
                          "Users Devices registered with Us. Notification will be sent to these users.",
                          "已存在資料庫的用戶"
                        )}{" "}
                        <span className="badge bg-info">
                          {csvMatches.length}
                        </span>
                      </h6>
                      <CSmartTable
                        columns={[
                          { key: "id", label: t("ID", "ID") },
                          { key: "name", label: t("Name", "名稱") },
                          { key: "email", label: t("Email", "電子郵件") },
                        ]}
                        items={csvMatches}
                        pagination
                        itemsPerPage={5}
                        sorter
                        scopedColumns={{
                          id: (item, index) => <td>{index + 1}</td>,
                        }}
                      />
                    </div>
                  )}
                </CTabPane>

                <CTabPane visible={tab === "list"}>
                  <div className="mb-2">
                    {t("Selected", "已選")} {selected.length} / {users.length}
                  </div>
                  <CSmartTable
                    columns={listColumns}
                    items={users}
                    scopedColumns={listScoped}
                    loading={loadingUsers}
                    tableFilter
                    pagination
                    itemsPerPageSelect
                    itemsPerPage={10}
                  />
                </CTabPane>
              </CTabContent>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={6}>
          <CCard className="h-100 d-flex flex-column">
            <CCardHeader>{t("Compose", "撰寫通知")}</CCardHeader>
            <CCardBody className="d-flex flex-column">
              <div className="mb-3">
                <CFormLabel>{t("Title", "標題")}</CFormLabel>
                <CFormInput
                  name="title"
                  value={notification.title}
                  onChange={handleInput}
                  placeholder={t("Enter title (32 characters)", "輸入標題")}
                  maxLength={32}
                />
              </div>
              <div className="mb-3">
                <CFormLabel>{t("Message", "訊息")}</CFormLabel>
                <CFormTextarea
                  name="message"
                  value={notification.message}
                  onChange={handleInput}
                  rows={4}
                  placeholder={t("Enter message (160 characters)", "輸入訊息")}
                  maxLength={162}
                />
              </div>
              <div className="mt-auto">
                <CButton
                  color="primary"
                  disabled={sending}
                  onClick={handleSend}
                >
                  {sending
                    ? t("Sending...", "發送中…")
                    : t("Send Notification", "發送通知")}
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <Toaster />
    </div>
  );
};

export default NotificationSender;
